import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, 
  CreditCard,
  Zap,
  CheckCircle,
  AlertCircle,
  Loader,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Mic,
  Users,
  FileText,
  Star,
  Trophy,
  Radio,
  X,
  Sparkles
} from 'lucide-react';
import { podcastApi, teamApi, API_BASE_URL } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveGuestDraft, loadGuestDraft, clearGuestDraft, GUEST_TEAM_ID } from '../utils/guestDraft';

const STEP_META = [
  { num: 1, label: 'Setup',       icon: Users },
  { num: 2, label: 'Games',       icon: FileText },
  { num: 3, label: 'Roster',      icon: Star },
  { num: 4, label: 'Highlights',  icon: Trophy },
];

const GAME_TYPES = [
  { id: 'single',       label: 'Single Game',  desc: 'One game recap',                       icon: FileText, min: 1, max: 1 },
  { id: 'doubleheader', label: 'Double Header', desc: 'Two games, same day',                  icon: Radio,    min: 2, max: 2 },
  { id: 'tournament',   label: 'Tournament',    desc: 'Pool play & bracket — multiple games', icon: Trophy,   min: 2, max: 8 },
];

const TOURNAMENT_LABELS = ['Pool Play', 'Quarterfinal', 'Semifinal', 'Championship', 'Bracket'];

let gameIdCounter = 0;
const blankGame = () => ({
  uid: ++gameIdCounter,
  opponentName: '',
  usersTeamScore: '',
  opponentScore: '',
  boxScores: [],
  boxScoreText: '',
  uploadMethod: 'file',
  gameLabel: '',
});

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {STEP_META.map((step, i) => {
      const done    = current > step.num;
      const active  = current === step.num;
      const Icon    = step.icon;
      return (
        <React.Fragment key={step.num}>
          <div className="flex flex-col items-center gap-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 ${
              done   ? 'bg-electric-green text-white shadow-md' :
              active ? 'bg-royal-blue text-white shadow-lg ring-4 ring-royal-blue/20' :
                       'bg-white/10 text-gray-500 dark:text-gray-300'
            }`}>
              {done ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
            </div>
            <span className={`text-xs font-semibold hidden sm:block ${active ? 'text-blue-300' : done ? 'text-electric-green' : 'text-gray-500 dark:text-gray-300'}`}>{step.label}</span>
          </div>
          {i < STEP_META.length - 1 && (
            <div className={`flex-1 h-1 rounded-full transition-all duration-500 max-w-[48px] ${current > step.num ? 'bg-electric-green' : 'bg-white/15 dark:bg-gray-700'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const CreatePage = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [teams, setTeams] = useState([]);
  const fileInputRefs = useRef({});
  const errorRef = useRef(null);
  // Guest flow: team entered before login (saved to DB after login), redirect state,
  // and a guard so the post-login resume only runs once.
  const [guestTeam, setGuestTeam] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const resumedRef = useRef(false);
  const [formData, setFormData] = useState({
    teamName: '',
    players: [],
    selectedTeamId: '',
    gameType: 'single',
    eventName: '',
    games: [blankGame()],
    highlights: '',
    termsAgreed: false,
  });
  const [draggingGame, setDraggingGame] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [podcastResult, setPodcastResult] = useState(null);
  const [error, setError] = useState('');
  const [loadingMessages, setLoadingMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('initializing');
  // Quick create team (inline in Step 1)
  const [quickTeam, setQuickTeam] = useState({ teamName: '', sport: '', roster: [{ playerName: '', playerNumber: '' }] });
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickMsg, setQuickMsg] = useState({ type: '', text: '' });
  const [showRosterDrawer, setShowRosterDrawer] = useState(false);
  const [drawerDismissed, setDrawerDismissed] = useState(false);

  const dismissDrawer = useCallback(() => {
    setShowRosterDrawer(false);
    setDrawerDismissed(true);
  }, []);

  const resetQuickTeam = () => setQuickTeam({ teamName: '', sport: '', roster: [{ playerName: '', playerNumber: '' }] });
  const handleQuickRosterChange = (index, field, value) => {
    setQuickTeam(prev => {
      const roster = [...prev.roster];
      roster[index] = { ...roster[index], [field]: value };
      return { ...prev, roster };
    });
  };
  const addQuickPlayer = () => setQuickTeam(prev => ({ ...prev, roster: [...prev.roster, { playerName: '', playerNumber: '' }] }));
  const removeQuickPlayer = (index) => setQuickTeam(prev => ({ ...prev, roster: prev.roster.filter((_, i) => i !== index) }));

  // Derived disabled state for Save button (must have team name, sport, and at least one player with a name)
  const hasRosterPlayer = (quickTeam.roster || []).some(r => (r.playerName || '').trim() !== '');
  const isQuickSaveDisabled = quickSaving || !quickTeam.teamName || !quickTeam.sport || !hasRosterPlayer;
  useEffect(() => {
    const fetchTeams = async () => {
      if (isAuthenticated) {
        try {
          const response = await teamApi.getTeams();
          setTeams(response.data);
          if (response.data.length === 0) {
            setShowRosterDrawer(true);
          }
        } catch (error) {
          console.error('Failed to fetch teams:', error);
        }
      }
    };
    fetchTeams();
  }, [isAuthenticated]);

  const refreshTeams = async () => {
    try {
      const response = await teamApi.getTeams();
      setTeams(response.data);
      return response.data;
    } catch (e) {
      console.error('Failed to refresh teams:', e);
      return [];
    }
  };

  useEffect(() => {
    const fetchLoadingMessages = async () => {
      try {
        const response = await podcastApi.getLoadingMessages();
        setLoadingMessages(response.data.messages);
      } catch (error) {
        console.error('Failed to fetch loading messages:', error);
        setLoadingMessages([
          '🎙️ Doing vocal warmups...',
          '📊 Looking deep into the advanced metrics...',
          '🏆 Analyzing championship-level plays...',
          '⚾ Studying the box score like a pro scout...'
        ]);
      }
    };
    fetchLoadingMessages();
  }, []);

  useEffect(() => {
    let interval;
    if (isGenerating && loadingMessages.length > 0) {
      interval = setInterval(() => {
        setCurrentMessageIndex(prevIndex => (prevIndex + 1) % loadingMessages.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isGenerating, loadingMessages.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Game type & per-game helpers ──
  const selectedType = GAME_TYPES.find(t => t.id === formData.gameType) || GAME_TYPES[0];
  const isMultiGame = formData.gameType !== 'single';

  const handleGameTypeSelect = (typeId) => {
    const type = GAME_TYPES.find(t => t.id === typeId) || GAME_TYPES[0];
    setFormData(prev => {
      let games = prev.games.slice(0, type.max);
      while (games.length < type.min) games.push(blankGame());
      if (games.length === 0) games = [blankGame()];
      return { ...prev, gameType: typeId, games };
    });
  };

  const updateGame = (index, patch) => {
    setFormData(prev => {
      const games = prev.games.map((g, i) => (i === index ? { ...g, ...patch } : g));
      return { ...prev, games };
    });
  };

  const addGame = () => {
    setFormData(prev => {
      if (prev.games.length >= selectedType.max) return prev;
      return { ...prev, games: [...prev.games, blankGame()] };
    });
  };

  const removeGame = (index) => {
    setFormData(prev => {
      if (prev.games.length <= 1) return prev;
      return { ...prev, games: prev.games.filter((_, i) => i !== index) };
    });
  };

  const handleGameScoreChange = (index, name, value) => {
    if (value === '' || value === null || value === undefined) {
      updateGame(index, { [name]: '' });
      return;
    }
    const num = Number.parseInt(value, 10);
    if (Number.isNaN(num)) return;
    updateGame(index, { [name]: Math.max(0, Math.min(99, num)) });
  };

  const handleTeamSelect = (e) => {
    const teamId = e.target.value;
    const selectedTeam = teams.find(team => team._id === teamId);
    if (selectedTeam) {
      setFormData(prev => ({
        ...prev,
        selectedTeamId: teamId,
        teamName: selectedTeam.teamName,
        players: selectedTeam.roster
      }));
    } else {
      setFormData(prev => ({ ...prev, selectedTeamId: '', teamName: '', players: [] }));
    }
  };

  const ACCEPTED_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/heic', 'image/heif', 'image/webp'];

  const addFilesToGame = (index, files) => {
    setFormData(prev => {
      const games = prev.games.map((g, i) => {
        if (i !== index) return g;
        return { ...g, boxScores: [...g.boxScores, ...files].slice(0, 1) };
      });
      return { ...prev, games };
    });
  };

  const handleGameFileUpload = (index, e) => {
    const files = Array.from(e.target.files);
    addFilesToGame(index, files);
    e.target.value = '';
  };

  const handleGameDragOver = (e) => { e.preventDefault(); };
  const handleGameDragLeave = (e) => { e.preventDefault(); setDraggingGame(null); };
  const handleGameDrop = (index, e) => {
    e.preventDefault();
    setDraggingGame(null);
    const files = Array.from(e.dataTransfer.files).filter(f => ACCEPTED_FILE_TYPES.includes(f.type));
    if (files.length > 0) addFilesToGame(index, files);
  };

  const removeFileFromGame = (gameIndex, fileIndex) => {
    setFormData(prev => {
      const games = prev.games.map((g, i) => {
        if (i !== gameIndex) return g;
        return { ...g, boxScores: g.boxScores.filter((_, fi) => fi !== fileIndex) };
      });
      return { ...prev, games };
    });
  };

  // Build the createPodcast payload from a formData-shaped object.
  const buildPayload = (fd) => ({
    teamId: fd.selectedTeamId,
    gameType: fd.gameType,
    eventName: fd.eventName,
    highlights: fd.highlights,
    games: (fd.games || []).map(g => ({
      opponentName: g.opponentName,
      usersTeamScore: g.usersTeamScore,
      opponentScore: g.opponentScore,
      gameLabel: g.gameLabel,
      files: g.boxScores,
      boxScoreText: g.boxScoreText,
    })),
  });

  // Run generation for an already-built payload (used by both direct submit and
  // the post-login resume path).
  const runGeneration = useCallback(async (payload) => {
    setIsGenerating(true);
    setError('');
    setProgress(0);
    setProgressStatus('initializing');
    setCurrentMessageIndex(0);
    try {
      const response = await podcastApi.createPodcast(payload, (p) => {
        const pct = p?.progress != null ? Math.round(p.progress) : 0;
        setProgress(Math.min(Math.max(pct, 1), 99));
        if (p?.statusLabel) setProgressStatus(p.statusLabel);
      });
      setProgress(100);
      setProgressStatus('completed');
      setPodcastResult(response.data);
      await refreshUser(); // Refresh user credits
    } catch (err) {
      setError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
      // Surface the message at the top so it isn't buried below a long form on mobile.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsGenerating(false);
    }
  }, [refreshUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guest flow: no account yet. Persist the whole request (incl. box-score
    // files) and send them through Google login. Generation resumes automatically
    // after auth — no API cost is incurred until they're logged in.
    if (!isAuthenticated) {
      setRedirecting(true);
      try {
        await saveGuestDraft(formData, guestTeam);
      } catch (err) {
        console.error('[CreatePage] Failed to save guest draft:', err);
      }
      window.location.href = `${API_BASE_URL}/auth/google`;
      return;
    }
    // No hard credit gate here: users without credits get one free preview-only
    // generation. The backend enforces the free-preview limit and returns a 402
    // (surfaced below) once it has been used.
    await runGeneration(buildPayload(formData));
  };

  // After a guest logs in, restore their draft, save their team to the DB, and
  // resume generation automatically.
  useEffect(() => {
    if (!isAuthenticated || resumedRef.current) return;
    resumedRef.current = true;

    (async () => {
      let draft;
      try {
        draft = await loadGuestDraft();
      } catch (err) {
        return;
      }
      if (!draft || !draft.formData) return;

      try {
        const payload = buildPayload(draft.formData);

        // Persist the guest-entered team to the database and use its real id.
        if (draft.guestTeam && (draft.guestTeam.teamName || '').trim()) {
          const finalRoster = (draft.guestTeam.roster || []).filter(r => (r.playerName || '').trim() !== '');
          try {
            const res = await teamApi.createTeam({
              teamName: draft.guestTeam.teamName,
              sport: draft.guestTeam.sport || '',
              roster: finalRoster,
            });
            const newTeamId = res?.data?._id;
            if (newTeamId) {
              payload.teamId = newTeamId;
            } else {
              const updated = await refreshTeams();
              const match = [...updated].reverse().find(t => t.teamName === draft.guestTeam.teamName);
              if (match) payload.teamId = match._id;
            }
          } catch (teamErr) {
            console.error('[CreatePage] Failed to save guest team after login:', teamErr);
          }
        }

        await clearGuestDraft();

        // Only generate if we resolved a real team id.
        if (payload.teamId && payload.teamId !== GUEST_TEAM_ID) {
          await runGeneration(payload);
        }
      } catch (err) {
        console.error('[CreatePage] Failed to resume guest draft:', err);
        await clearGuestDraft();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isGameComplete = (g) => {
    if (!g.opponentName || !g.opponentName.trim()) return false;
    if (g.uploadMethod === 'file') return g.boxScores.length > 0;
    const len = (g.boxScoreText || '').trim().length;
    const uts = g.usersTeamScore;
    const ops = g.opponentScore;
    const scoresProvided = uts !== '' && ops !== '';
    const scoresValid = Number.isInteger(Number(uts)) && Number.isInteger(Number(ops)) &&
      Number(uts) >= 0 && Number(uts) <= 99 && Number(ops) >= 0 && Number(ops) <= 99;
    return len >= 50 && scoresProvided && scoresValid;
  };

  // Human-readable list of what's still needed for a game to be valid.
  // Drives the inline "what's missing" hints on step 2.
  const getGameMissing = (g) => {
    const missing = [];
    if (!g.opponentName || !g.opponentName.trim()) missing.push('Opponent Name');
    if (g.uploadMethod === 'file') {
      if (!g.boxScores || g.boxScores.length === 0) missing.push('Photo/PDF of box score OR Just Describe what happened');
    } else {
      const uts = g.usersTeamScore;
      const ops = g.opponentScore;
      const scoresProvided = uts !== '' && ops !== '';
      const scoresValid = scoresProvided &&
        Number.isInteger(Number(uts)) && Number.isInteger(Number(ops)) &&
        Number(uts) >= 0 && Number(uts) <= 99 && Number(ops) >= 0 && Number(ops) <= 99;
      if (!scoresValid) missing.push('Final score for both teams (0–99)');
      const len = (g.boxScoreText || '').trim().length;
      if (len < 50) missing.push(`Game description (${50 - len} more characters)`);
    }
    return missing;
  };

  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return Boolean(formData.selectedTeamId && formData.gameType);
      case 2:
        return formData.games.length > 0 && formData.games.every(isGameComplete);
      case 3: return true;
      case 4: return Boolean(formData.termsAgreed);
      default: return true;
    }
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-blue-950 to-gray-900 flex flex-col items-center justify-center px-4 py-16">
        {/* Glowing mic icon */}
        <div className="relative mb-8">
          <div className="absolute inset-0 rounded-full bg-royal-blue opacity-30 blur-2xl animate-pulse-slow scale-150" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-royal-blue to-blue-400 flex items-center justify-center shadow-2xl">
            <Radio className="h-12 w-12 text-white animate-pulse" />
          </div>
        </div>

        <h2 className="text-3xl sm:text-4xl font-black text-white mb-2 tracking-tight text-center">Generating Your Podcast</h2>
        <p className="text-blue-300 text-lg font-medium mb-8 text-center">{loadingMessages[currentMessageIndex] || '🎙️ Setting up the booth...'}</p>

        {/* Progress bar */}
        <div className="w-full max-w-lg mb-3">
          <div className="h-4 bg-gray-800 rounded-full overflow-hidden border border-gray-700">
            <div
              className="h-4 bg-gradient-to-r from-royal-blue to-electric-green rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex justify-between w-full max-w-lg text-sm mb-10">
          <span className="text-gray-400 font-medium">{progressStatus || 'Starting up...'}</span>
          <span className="text-electric-green font-bold">{Math.min(progress, 100)}%</span>
        </div>

        <p className="text-gray-400 text-sm text-center max-w-md mb-6">
          This can take up to 45 minutes. Feel free to check back on the{' '}
          <Link to="/my-podcasts" className="text-blue-400 underline hover:text-blue-300">My Podcasts</Link> page later.
        </p>
        <Link to="/my-podcasts" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200">
          <Radio className="h-4 w-4" />Go to My Podcasts
        </Link>
      </div>
    );
  }

  if (podcastResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-green-950 to-gray-900 flex flex-col items-center justify-center px-4 py-16 text-center">
        <div className="relative mb-8">
          <div className="absolute inset-0 rounded-full bg-electric-green opacity-25 blur-2xl scale-150" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-electric-green to-green-300 flex items-center justify-center shadow-2xl">
            <Trophy className="h-12 w-12 text-white" />
          </div>
        </div>
        <h2 className="text-4xl font-black text-white mb-3 tracking-tight">Your Podcast is Ready!</h2>
        <p className="text-green-300 text-lg mb-10 max-w-md">Your AI-powered GameCast has been created. Time to share the buzz!</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to={`/my-podcasts/${podcastResult._id}`} className="btn-primary inline-flex items-center gap-2 text-base px-8 py-3">
            <Mic className="h-5 w-5" />Listen Now
          </Link>
          <button
            onClick={() => { setPodcastResult(null); setCurrentStep(1); }}
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 text-base"
          >
            <Zap className="h-5 w-5" />Create Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">
      {/* Option B: First-visit bottom drawer / modal */}
      {showRosterDrawer && !drawerDismissed && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
            onClick={dismissDrawer}
            aria-hidden="true"
          />
          {/* Drawer — slides up from bottom on mobile, centered modal on desktop */}
          <div className="fixed z-50 bottom-0 left-0 right-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4 pointer-events-none">
            <div className="pointer-events-auto w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-slide-up">
              {/* Pill handle — mobile only */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="bg-gradient-to-r from-royal-blue to-blue-700 px-6 pt-4 pb-5 relative">
                <button
                  type="button"
                  onClick={dismissDrawer}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4 text-white" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-400 flex items-center justify-center shrink-0 shadow-lg">
                    <Mic className="h-6 w-6 text-gray-900" />
                  </div>
                  <div>
                    <p className="text-white font-black text-lg leading-tight">Welcome to Postgame Buzz!</p>
                    <p className="text-blue-200 text-sm mt-0.5">One quick thing before you start</p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-6 py-5">
                <p className="text-gray-800 font-semibold text-base mb-4">
                  To create your first podcast, you'll need to set up your team roster first.
                </p>
                <ul className="space-y-3 mb-5">
                  {[
                    { emoji: '🎙️', text: 'Your AI hosts call each player by name during the podcast' },
                    { emoji: '⚡', text: 'Only takes 2 minutes — you\'ll reuse it for every future game' },
                    { emoji: '✏️', text: 'You can always edit your roster anytime from the Teams page' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center text-base shrink-0">{item.emoji}</span>
                      <span className="mt-1">{item.text}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/teams/new"
                  className="w-full inline-flex items-center justify-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-black py-3.5 px-6 rounded-xl transition-colors text-base shadow-lg"
                  onClick={dismissDrawer}
                >
                  <Users className="h-5 w-5" />Got it — Set Up My Roster
                </Link>
                <button
                  type="button"
                  onClick={dismissDrawer}
                  className="w-full mt-2 text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Dark hero header banner */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 border-b-4 border-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg">
                <Mic className="h-5 w-5 text-gray-900" />
              </div>
              <span className="text-yellow-400 font-bold text-sm uppercase tracking-widest">Studio</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Create Your GameCast</h1>
            <p className="text-blue-300 mt-1 text-sm">4 quick steps — your AI-powered sports podcast awaits</p>
          </div>
          {/* Credit pill */}
          <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-5 py-3 shrink-0">
            <CreditCard className="h-5 w-5 text-yellow-400" />
            <div>
              <p className="text-white/60 text-xs font-medium uppercase tracking-wide">Your Credits</p>
              <p className={`text-xl font-black ${(user?.credits ?? 0) > 0 ? 'text-electric-green' : 'text-red-400'}`}>{user?.credits ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Prominent error banner — kept at the top so it's never buried on mobile */}
        {error && (
          <div ref={errorRef} className="mb-6 flex items-start gap-3 bg-red-600/90 border border-red-400 text-white px-5 py-4 rounded-2xl shadow-lg">
            <AlertCircle className="h-6 w-6 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm sm:text-base leading-snug">{error}</p>
              {error.toLowerCase().includes('free preview') && (
                <Link to="/#pricing" className="inline-block mt-2 bg-white text-red-700 font-black text-sm py-1.5 px-4 rounded-lg hover:bg-red-50 transition-colors">
                  Buy Credits
                </Link>
              )}
            </div>
            <button
              type="button"
              onClick={() => setError('')}
              className="shrink-0 text-white/80 hover:text-white text-xl leading-none font-bold px-1"
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main form card */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-blue-700/30 overflow-hidden">
              {/* Step progress bar inside card header */}
              <div className="bg-white/5 border-b border-white/10 px-8 pt-6 pb-4">
                <StepIndicator current={currentStep} total={4} />
              </div>

              <div className="px-8 py-6">
                <form onSubmit={handleSubmit}>
                  {/* ── STEP 1 ── */}
                  {currentStep === 1 && (
                    <section>
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h2 className="text-2xl font-black text-white">Select Your Team &amp; Opponent</h2>
                          <p className="text-gray-400 text-sm mt-1">Pick your team and enter the opponent name to get started.</p>
                        </div>
                        <Link to="/teams" className="shrink-0 ml-4 text-sm font-semibold text-blue-400 hover:text-blue-300 underline-offset-2 hover:underline">Manage Teams</Link>
                      </div>

                      {/* Option A: empty-state banner when no teams exist */}
                      {teams.length === 0 && !guestTeam && (
                        <div className="mt-5 rounded-2xl bg-gradient-to-r from-royal-blue to-blue-700 p-5 text-white shadow-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                              <Sparkles className="h-5 w-5 text-yellow-300" />
                            </div>
                            <div>
                              <p className="font-black text-base leading-tight">First, create your team &amp; roster</p>
                              <p className="text-blue-200 text-xs mt-0.5">Your players get called by name in the podcast — it only takes a minute.</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              document.getElementById('inline-create-team')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            className="mt-2 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black text-sm py-2.5 px-5 rounded-xl transition-colors"
                          >
                            <Users className="h-4 w-4" />Set Up My Team Below
                          </button>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                        <div>
                          <label htmlFor="teamName" className="block text-sm font-semibold text-gray-300 mb-2">Your Team</label>
                          {formData.selectedTeamId === GUEST_TEAM_ID ? (
                            <div className="w-full bg-electric-green/10 border-2 border-electric-green/40 rounded-xl py-3 px-4 text-white text-sm font-medium flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 shrink-0 text-electric-green" />
                              <span className="font-bold">{formData.teamName}</span>
                              <span className="text-gray-400">selected — edit below</span>
                            </div>
                          ) : teams.length === 0 ? (
                            <div className="w-full bg-white/5 border-2 border-dashed border-white/20 rounded-xl py-3 px-4 text-gray-500 text-sm font-medium flex items-center gap-2">
                              <Users className="h-4 w-4 shrink-0" />Create your team below first
                            </div>
                          ) : (
                            <div className="relative">
                              <select
                                id="teamName"
                                name="selectedTeamId"
                                value={formData.selectedTeamId}
                                onChange={handleTeamSelect}
                                className="w-full appearance-none bg-blue-950 border-2 border-white/20 focus:border-blue-400 rounded-xl py-3 pl-4 pr-10 text-white font-medium transition-colors outline-none"
                                required
                              >
                                <option value="" disabled style={{ background: '#0f172a', color: '#fff' }}>Select your team...</option>
                                {teams.map(team => (
                                  <option key={team._id} value={team._id} style={{ background: '#0f172a', color: '#fff' }}>{team.teamName}</option>
                                ))}
                              </select>
                              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Game type selector */}
                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-300 mb-2">What are we recapping?</label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {GAME_TYPES.map(type => {
                            const Icon = type.icon;
                            const active = formData.gameType === type.id;
                            return (
                              <button
                                key={type.id}
                                type="button"
                                onClick={() => handleGameTypeSelect(type.id)}
                                className={`text-left rounded-2xl border-2 p-4 transition-all duration-200 ${active ? 'border-yellow-400 bg-yellow-400/10 shadow-lg' : 'border-white/15 bg-white/5 hover:border-blue-400 hover:bg-blue-500/10'}`}
                              >
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${active ? 'bg-yellow-400 text-gray-900' : 'bg-white/10 text-blue-300'}`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                                <p className="font-black text-white text-sm">{type.label}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{type.desc}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Event name for multi-game */}
                      {isMultiGame && (
                        <div className="mt-5">
                          <label htmlFor="eventName" className="block text-sm font-semibold text-gray-300 mb-2">
                            {formData.gameType === 'tournament' ? 'Tournament Name' : 'Event Name'} <span className="text-gray-500 font-normal">(optional)</span>
                          </label>
                          <input
                            type="text"
                            id="eventName"
                            name="eventName"
                            value={formData.eventName}
                            onChange={handleInputChange}
                            className="w-full bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl py-3 px-4 text-white placeholder-white/60 font-medium transition-colors outline-none"
                            placeholder={formData.gameType === 'tournament' ? 'e.g., Memorial Day Classic' : 'e.g., Tuesday Double Header'}
                          />
                          <p className="text-xs text-gray-500 mt-2">You'll add each opponent and box score on the next step — one per game.</p>
                        </div>
                      )}

                      {/* Option D: hint when Next is disabled */}
                      {!canGoNext() && (
                        <div className="mt-5 flex items-start gap-2 bg-amber-900/30 border border-amber-500/30 rounded-xl px-4 py-3 text-amber-300 text-sm">
                          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                          <span>
                            {teams.length === 0
                              ? <><strong>Create your team &amp; roster first</strong> — use the form below. Your roster is how the AI hosts call players by name in the podcast!</>
                              : <><strong>Select your team</strong> from the dropdown to continue.</>
                            }
                          </span>
                        </div>
                      )}

                      <div className="flex justify-end mt-4 mb-2">
                        <button type="button" onClick={nextStep} disabled={!canGoNext()} title={!canGoNext() ? 'Complete the highlighted fields to continue' : undefined} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none">
                          Next Step <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Inline Create Team */}
                      <div id="inline-create-team" className="mt-6 rounded-2xl border-2 border-dashed border-blue-500/30 bg-blue-900/20 p-5">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-5 w-5 text-blue-400" />
                          <h3 className="text-lg font-black text-white">Create New Team</h3>
                          <span className="ml-1 text-xs font-bold text-blue-300 bg-blue-500/20 border border-blue-400/30 rounded-full px-2 py-0.5">First-time setup</span>
                        </div>
                        <p className="text-sm text-gray-400 mb-4">Do this once — we'll remember your team for every future podcast.</p>

                        {quickMsg.text && (
                          <div className={`mb-4 text-sm rounded-xl px-4 py-3 flex items-center gap-2 ${quickMsg.type === 'error' ? 'bg-red-900/30 text-red-300 border border-red-500/30' : 'bg-green-900/30 text-green-300 border border-green-500/30'}`}>
                            {quickMsg.type === 'error' ? <AlertCircle className="h-4 w-4 shrink-0" /> : <CheckCircle className="h-4 w-4 shrink-0" />}
                            {quickMsg.text}
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wide">Team Name</label>
                            <input
                              type="text"
                              value={quickTeam.teamName}
                              onChange={(e) => setQuickTeam(prev => ({ ...prev, teamName: e.target.value }))}
                              className="w-full bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl py-2.5 px-3 text-sm text-white placeholder-white/60 outline-none transition-colors"
                              placeholder="e.g., Bayside Tigers"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wide">Sport</label>
                            <select
                              value={quickTeam.sport}
                              onChange={(e) => setQuickTeam(prev => ({ ...prev, sport: e.target.value }))}
                              className="w-full bg-blue-950 border-2 border-white/20 focus:border-blue-400 rounded-xl py-2.5 px-3 text-sm text-white outline-none transition-colors"
                            >
                              <option value="" style={{ background: '#0f172a', color: '#fff' }}>Select a sport</option>
                              <option value="Baseball" style={{ background: '#0f172a', color: '#fff' }}>Baseball</option>
                              <option value="Softball" style={{ background: '#0f172a', color: '#fff' }}>Softball</option>
                              <option value="Soccer" style={{ background: '#0f172a', color: '#fff' }}>Soccer</option>
                              <option value="Football" style={{ background: '#0f172a', color: '#fff' }}>Football</option>
                              <option value="Basketball" style={{ background: '#0f172a', color: '#fff' }}>Basketball</option>
                            </select>
                          </div>
                        </div>

                        <div className="mt-4 bg-white/5 rounded-xl border border-white/10 p-4">
                          <h4 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-1"><Star className="h-4 w-4 text-yellow-400" />Roster</h4>
                          {quickTeam.roster.map((p, idx) => (
                            <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center mb-2">
                              <input
                                type="text"
                                placeholder="Player Name"
                                value={p.playerName}
                                onChange={(e) => handleQuickRosterChange(idx, 'playerName', e.target.value)}
                                className="md:col-span-2 bg-white/10 border border-white/20 focus:border-blue-400 rounded-lg py-2 px-3 text-sm text-white placeholder-white/60 outline-none w-full"
                              />
                              <input
                                type="text"
                                placeholder="#"
                                value={p.playerNumber}
                                onChange={(e) => handleQuickRosterChange(idx, 'playerNumber', e.target.value)}
                                className="bg-white/10 border border-white/20 focus:border-blue-400 rounded-lg py-2 px-3 text-sm text-white placeholder-white/60 outline-none w-full"
                              />
                              <button
                                type="button"
                                onClick={() => removeQuickPlayer(idx)}
                                className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/20 disabled:opacity-30 transition-colors flex items-center justify-center"
                                disabled={quickTeam.roster.length === 1}
                                aria-label="Remove player"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={addQuickPlayer} className="mt-1 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors">+ Add Player</button>
                          {!(quickTeam.roster || []).some(r => (r.playerName || '').trim() !== '') && (
                            <p className="mt-1 text-xs text-gray-500">Add at least one player to enable Save.</p>
                          )}
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                          <p className="text-xs text-gray-500">Prefer the full editor? <Link to="/teams" className="text-blue-400 hover:underline">Manage Teams</Link></p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => { setQuickMsg({ type: '', text: '' }); resetQuickTeam(); }}
                                className="text-sm font-semibold text-gray-300 hover:text-white bg-white/10 border border-white/20 rounded-lg px-4 py-2 transition-colors"
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-2 text-sm font-bold rounded-lg px-4 py-2 transition-all duration-200 ${isQuickSaveDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-royal-blue text-white hover:bg-blue-800 shadow-md hover:shadow-lg'}`}
                              disabled={isQuickSaveDisabled}
                              title={isQuickSaveDisabled ? 'Enter team name, sport, and at least one player' : 'Save Team'}
                              onClick={async () => {
                                setQuickMsg({ type: '', text: '' });
                                const finalRoster = (quickTeam.roster || []).filter(r => (r.playerName || '').trim() !== '');

                                // Guest (not logged in): keep the team client-side.
                                // It's saved to the database automatically after login.
                                if (!isAuthenticated) {
                                  setGuestTeam({ teamName: quickTeam.teamName, sport: quickTeam.sport, roster: finalRoster });
                                  setFormData(prev => ({ ...prev, selectedTeamId: GUEST_TEAM_ID, teamName: quickTeam.teamName, players: finalRoster }));
                                  resetQuickTeam();
                                  setQuickMsg({ type: 'success', text: "Team saved! Finish the steps — you'll sign in at the end to generate your podcast." });
                                  return;
                                }

                                setQuickSaving(true);
                                try {
                                  await teamApi.createTeam({ teamName: quickTeam.teamName, sport: quickTeam.sport, roster: finalRoster });
                                  const updated = await refreshTeams();
                                  const newTeam = [...updated].reverse().find(t => t.teamName === quickTeam.teamName);
                                  if (newTeam) {
                                    setFormData(prev => ({ ...prev, selectedTeamId: newTeam._id, teamName: newTeam.teamName, players: newTeam.roster || [] }));
                                  }
                                  resetQuickTeam();
                                  setShowRosterDrawer(false);
                                  setDrawerDismissed(true);
                                  setQuickMsg({ type: 'success', text: 'Team created and selected! You can refine the roster anytime in Manage Teams.' });
                                } catch (err) {
                                  console.error(err);
                                  setQuickMsg({ type: 'error', text: err.response?.data?.error || 'Failed to create team. Please try again.' });
                                } finally {
                                  setQuickSaving(false);
                                }
                              }}
                            >
                              {quickSaving ? <><Loader className="h-4 w-4 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4" />Save Team</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── STEP 2 ── */}
                  {currentStep === 2 && (
                    <section>
                      <h2 className="text-2xl font-black text-white mb-1">{isMultiGame ? 'Game-by-Game Recap' : 'Game Info'}</h2>
                      <p className="text-gray-400 text-sm mb-6">
                        {isMultiGame
                          ? 'Add each game you played. For every game, enter the opponent and final score, then upload the box score (PDF or screenshot) — or just describe what happened.'
                          : "Enter the opponent and final score, then upload the box score PDF or screenshot. Don't have the box score? Just describe what happened in the game."}
                      </p>

                      {/* What's still needed — pinned to the top so it's easy to see on mobile */}
                      {(() => {
                        const incomplete = formData.games
                          .map((g, i) => ({ index: i, label: g.gameLabel || g.opponentName, missing: getGameMissing(g) }))
                          .filter((g) => g.missing.length > 0);
                        if (incomplete.length === 0) return null;
                        return (
                          <div className="mb-6 rounded-2xl bg-yellow-400 text-gray-900 p-5 shadow-lg shadow-yellow-400/30">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="h-4 w-4 text-gray-900" strokeWidth={2.5} />
                              <span className="text-[11px] font-black uppercase tracking-[0.22em] text-gray-900/80">Almost there</span>
                            </div>
                            <p className="text-lg sm:text-xl font-black tracking-tight leading-snug mb-4">
                              A few things left before you can continue
                            </p>
                            <div className="space-y-3.5">
                              {incomplete.map((g) => (
                                <div key={g.index}>
                                  {isMultiGame && (
                                    <p className="text-[11px] font-black text-gray-900/70 uppercase tracking-[0.15em] mb-1.5">
                                      Game {g.index + 1}{g.label ? ` · ${g.label}` : ''}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap gap-2">
                                    {g.missing.map((m) => (
                                      <span key={m} className="inline-flex items-center gap-1.5 bg-gray-900 text-white text-xs font-bold rounded-full pl-2.5 pr-3 py-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />{m}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="space-y-5">
                        {formData.games.map((game, gi) => {
                          const fileInputId = `game-file-${game.uid}`;
                          const complete = isGameComplete(game);
                          return (
                            <div key={game.uid} className={`rounded-2xl border-2 p-5 transition-colors ${complete ? 'border-electric-green/40 bg-electric-green/5' : 'border-white/15 bg-white/5'}`}>
                              {/* Card header */}
                              {isMultiGame && (
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${complete ? 'bg-electric-green text-white' : 'bg-blue-500/20 text-blue-300'}`}>
                                      {complete ? <CheckCircle className="h-4 w-4" /> : gi + 1}
                                    </span>
                                    <h3 className="font-black text-white text-base">Game {gi + 1}</h3>
                                  </div>
                                  {formData.games.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeGame(gi)}
                                      className="text-gray-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                                      aria-label="Remove game"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              )}

                              {/* Opponent + optional round label */}
                              <div className={`grid grid-cols-1 ${formData.gameType === 'tournament' ? 'md:grid-cols-2' : ''} gap-3 mb-4`}>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">Opponent</label>
                                  <input
                                    type="text"
                                    value={game.opponentName}
                                    onChange={(e) => updateGame(gi, { opponentName: e.target.value })}
                                    className="w-full bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl py-2.5 px-3 text-sm text-white placeholder-white/60 font-medium outline-none transition-colors"
                                    placeholder="e.g., Valley Bulldogs"
                                  />
                                </div>
                                {formData.gameType === 'tournament' && (
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">Round <span className="text-gray-600 font-normal lowercase">(optional)</span></label>
                                    <input
                                      type="text"
                                      list="tournament-rounds"
                                      value={game.gameLabel}
                                      onChange={(e) => updateGame(gi, { gameLabel: e.target.value })}
                                      className="w-full bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-xl py-2.5 px-3 text-sm text-white placeholder-white/60 font-medium outline-none transition-colors"
                                      placeholder="e.g., Pool Play"
                                    />
                                  </div>
                                )}
                              </div>

                              {/* Score board */}
                              <div className="bg-gradient-to-r from-gray-900 to-blue-950 rounded-2xl p-4 mb-4">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 text-center">Final Score</p>
                                <div className="grid grid-cols-3 gap-3 items-center">
                                  <div className="text-center">
                                    <p className="text-white text-xs font-bold mb-1.5 truncate">{formData.teamName || 'Your Team'}</p>
                                    <input
                                      type="number" min="0" max="99"
                                      value={game.usersTeamScore}
                                      onChange={(e) => handleGameScoreChange(gi, 'usersTeamScore', e.target.value)}
                                      className="w-full bg-white/10 border-2 border-white/20 focus:border-yellow-400 rounded-xl py-2.5 text-center text-2xl font-black text-white outline-none transition-colors"
                                      placeholder="0"
                                    />
                                  </div>
                                  <div className="text-center">
                                    <span className="text-2xl font-black text-yellow-400">VS</span>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-white text-xs font-bold mb-1.5 truncate">{game.opponentName || 'Opponent'}</p>
                                    <input
                                      type="number" min="0" max="99"
                                      value={game.opponentScore}
                                      onChange={(e) => handleGameScoreChange(gi, 'opponentScore', e.target.value)}
                                      className="w-full bg-white/10 border-2 border-white/20 focus:border-yellow-400 rounded-xl py-2.5 text-center text-2xl font-black text-white outline-none transition-colors"
                                      placeholder="0"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Tab switcher */}
                              <div className="flex bg-white/10 rounded-xl p-1 mb-4">
                                <button
                                  type="button"
                                  onClick={() => updateGame(gi, { uploadMethod: 'file' })}
                                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${game.uploadMethod === 'file' ? 'bg-white/20 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
                                >
                                  <Upload className="h-4 w-4" />Upload File
                                </button>
                                <button
                                  type="button"
                                  onClick={() => updateGame(gi, { uploadMethod: 'text' })}
                                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${game.uploadMethod === 'text' ? 'bg-white/20 text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
                                >
                                  <FileText className="h-4 w-4" />Describe Game
                                </button>
                              </div>

                              {game.uploadMethod === 'file' ? (
                                <div className="space-y-3">
                                  <div
                                    className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-200 ${game.boxScores.length === 1 ? 'border-electric-green bg-electric-green/10 cursor-default' : draggingGame === gi ? 'border-blue-400 bg-blue-500/20' : 'border-white/20 bg-white/5 hover:border-blue-400 hover:bg-blue-500/10'}`}
                                    onClick={() => game.boxScores.length < 1 && fileInputRefs.current[fileInputId]?.click()}
                                    onDragOver={game.boxScores.length < 1 ? handleGameDragOver : undefined}
                                    onDragEnter={game.boxScores.length < 1 ? () => setDraggingGame(gi) : undefined}
                                    onDragLeave={game.boxScores.length < 1 ? handleGameDragLeave : undefined}
                                    onDrop={game.boxScores.length < 1 ? (e) => handleGameDrop(gi, e) : undefined}
                                  >
                                    <input
                                      type="file"
                                      ref={(el) => { fileInputRefs.current[fileInputId] = el; }}
                                      onChange={(e) => handleGameFileUpload(gi, e)}
                                      className="hidden"
                                      accept=".pdf,image/png,image/jpeg,image/heic,image/heif,image/webp"
                                    />
                                    {game.boxScores.length === 0 ? (
                                      <>
                                        <Upload className="mx-auto h-9 w-9 text-gray-400 mb-2" />
                                        <p className="font-semibold text-gray-300 text-sm">Upload box score file</p>
                                        <p className="text-xs text-gray-500 mt-1">PDF from GameChanger (preferred) <span className="mx-1">•</span> Or a photo/screenshot</p>
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="mx-auto h-7 w-7 text-electric-green mb-1" />
                                        <p className="font-semibold text-electric-green text-sm">File uploaded</p>
                                      </>
                                    )}
                                  </div>

                                  {game.boxScores.length > 0 && (
                                    <div className="space-y-2">
                                      {game.boxScores.map((file, fidx) => (
                                        <div key={fidx} className="flex items-center gap-3 bg-electric-green/10 border border-electric-green/30 rounded-xl px-4 py-2.5">
                                          <CheckCircle className="h-4 w-4 text-electric-green shrink-0" />
                                          <span className="text-sm font-medium text-green-300 truncate flex-1">{file.name}</span>
                                          <button
                                            type="button"
                                            onClick={() => removeFileFromGame(gi, fidx)}
                                            className="ml-auto text-green-500 hover:text-red-500 transition-colors shrink-0"
                                            aria-label="Remove file"
                                          >
                                            <X className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="relative">
                                  <textarea
                                    value={game.boxScoreText}
                                    onChange={(e) => updateGame(gi, { boxScoreText: e.target.value })}
                                    className={`w-full h-36 bg-white/10 border-2 rounded-2xl py-3 px-4 text-sm text-white placeholder-white/60 outline-none resize-none transition-colors ${(game.boxScoreText || '').length < 50 ? 'border-orange-500/50 focus:border-orange-400' : 'border-electric-green/50 focus:border-electric-green'}`}
                                    placeholder="Tell us what happened... Who scored? Any big plays? Be as detailed as you like!"
                                  />
                                  <div className={`absolute bottom-3 right-3 text-xs px-2 py-1 rounded-lg font-semibold ${(game.boxScoreText || '').length < 50 ? 'bg-orange-900/50 text-orange-300' : 'bg-green-900/50 text-green-300'}`}>
                                    {(game.boxScoreText || '').length}/50 min
                                    {(game.boxScoreText || '').length < 50 && ` (${50 - (game.boxScoreText || '').length} more)`}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <datalist id="tournament-rounds">
                        {TOURNAMENT_LABELS.map(l => <option key={l} value={l} />)}
                      </datalist>

                      {/* Add game button */}
                      {isMultiGame && formData.games.length < selectedType.max && (
                        <button
                          type="button"
                          onClick={addGame}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-blue-500/40 hover:border-blue-400 bg-blue-900/10 hover:bg-blue-500/10 text-blue-300 font-bold py-3 rounded-2xl transition-colors"
                        >
                          <span className="text-lg leading-none">+</span> Add Another Game
                        </button>
                      )}
                      {isMultiGame && formData.games.length >= selectedType.max && (
                        <p className="mt-3 text-center text-xs text-gray-500">Maximum of {selectedType.max} games reached.</p>
                      )}
                    </section>
                  )}

                  {/* ── STEP 3 ── */}
                  {currentStep === 3 && (
                    <section>
                      <h2 className="text-2xl font-black text-white mb-1">Review Roster</h2>
                      <p className="text-gray-400 text-sm mb-5">These are the players your AI commentators will call by name.</p>

                      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                        {formData.players.length > 0 ? (
                          <ul className="divide-y divide-white/10">
                            {formData.players.map((player, i) => (
                              <li key={player._id} className="flex items-center gap-3 px-5 py-3">
                                <span className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-300 text-xs font-black flex items-center justify-center">#{player.playerNumber || i + 1}</span>
                                <span className="font-semibold text-gray-200 text-sm">{player.playerName}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-center py-10 px-6">
                            <Users className="mx-auto h-10 w-10 text-gray-300 mb-2" />
                            <p className="font-semibold text-gray-400">No players on this roster yet.</p>
                            <p className="text-sm text-gray-500 mt-1">Add players so the podcast can reference them by name.</p>
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-gray-400 mt-3">
                        Need changes?{' '}
                        {formData.selectedTeamId
                          ? <Link to={`/teams/edit/${formData.selectedTeamId}`} className="text-blue-400 hover:underline font-medium">Edit roster</Link>
                          : <Link to="/teams/create" className="text-blue-400 hover:underline font-medium">Create team</Link>
                        }
                      </p>
                    </section>
                  )}

                  {/* ── STEP 4 ── */}
                  {currentStep === 4 && (
                    <section>
                      <h2 className="text-2xl font-black text-white mb-1">Add Highlights <span className="text-base font-medium text-gray-500">(optional)</span></h2>
                      <p className="text-gray-400 text-sm mb-5">Tell the commentators which moments to spotlight — walk-off hits, hat tricks, big saves, etc.</p>

                      <div className="relative">
                        <textarea
                          id="highlights"
                          name="highlights"
                          value={formData.highlights}
                          onChange={handleInputChange}
                          className="w-full h-28 bg-white/10 border-2 border-white/20 focus:border-blue-400 rounded-2xl py-3 px-4 text-sm text-white placeholder-white/60 outline-none resize-none transition-colors"
                          placeholder="e.g., Jake hit a walk-off homer in the 9th. Maria had a hat-trick. The defense made 3 double plays."
                        />
                      </div>

                      {/* Summary card */}
                      <div className="mt-5 bg-gradient-to-r from-gray-900 to-blue-950 rounded-2xl p-5">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Your GameCast Summary</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-white/10 rounded-xl px-4 py-3">
                            <p className="text-gray-400 text-xs mb-1">Team</p>
                            <p className="text-white font-bold truncate">{formData.teamName || '—'}</p>
                          </div>
                          <div className="bg-white/10 rounded-xl px-4 py-3">
                            <p className="text-gray-400 text-xs mb-1">{isMultiGame ? 'Format' : 'Opponent'}</p>
                            <p className="text-white font-bold truncate">
                              {isMultiGame ? selectedType.label : (formData.games[0]?.opponentName || '—')}
                            </p>
                          </div>
                          <div className="bg-white/10 rounded-xl px-4 py-3">
                            <p className="text-gray-400 text-xs mb-1">{isMultiGame ? 'Games' : 'Score'}</p>
                            <p className="text-white font-bold">
                              {isMultiGame
                                ? formData.games.length
                                : (formData.games[0]?.usersTeamScore !== '' && formData.games[0]?.usersTeamScore != null
                                    ? `${formData.games[0].usersTeamScore} – ${formData.games[0].opponentScore}`
                                    : '—')}
                            </p>
                          </div>
                          <div className="bg-white/10 rounded-xl px-4 py-3">
                            <p className="text-gray-400 text-xs mb-1">Players</p>
                            <p className="text-white font-bold">{formData.players.length}</p>
                          </div>
                        </div>
                        {isMultiGame && (formData.eventName || '').trim() && (
                          <div className="mt-3 bg-white/10 rounded-xl px-4 py-3">
                            <p className="text-gray-400 text-xs mb-1">{formData.gameType === 'tournament' ? 'Tournament' : 'Event'}</p>
                            <p className="text-white font-bold truncate">{formData.eventName}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-start mt-5 p-4 bg-blue-900/30 rounded-xl border border-blue-500/30">
                        <input
                          type="checkbox"
                          id="termsAgreed"
                          name="termsAgreed"
                          checked={formData.termsAgreed}
                          onChange={handleInputChange}
                          className="mt-0.5 mr-3 h-4 w-4 text-royal-blue border-gray-300 rounded focus:ring-royal-blue"
                          required
                        />
                        <label htmlFor="termsAgreed" className="text-sm text-gray-300">
                          I agree to the <Link to="/terms" className="text-blue-400 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and understand this podcast is AI-generated.
                        </label>
                      </div>
                    </section>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-8 pt-6 border-t border-white/10">
                    <div>
                      {currentStep > 1 && (
                        <button type="button" onClick={prevStep} className="inline-flex items-center gap-2 text-sm font-bold text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl py-2.5 px-5 transition-colors">
                          <ChevronLeft className="h-4 w-4" />Back
                        </button>
                      )}
                    </div>
                    <div>
                      {currentStep < 4 && currentStep !== 1 && (
                        <button type="button" onClick={nextStep} disabled={!canGoNext()} title={!canGoNext() ? 'Complete the highlighted fields to continue' : undefined} className="btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none">
                          Next Step <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                      {currentStep === 4 && (
                        <button type="submit" disabled={isGenerating || redirecting || !formData.termsAgreed} className="btn-primary inline-flex items-center gap-2 text-base px-8 py-3 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none">
                          {redirecting
                            ? <><Loader className="h-5 w-5 animate-spin" />Redirecting to Sign In…</>
                            : isAuthenticated
                              ? <><Zap className="h-5 w-5" />Make My Podcast</>
                              : <><Zap className="h-5 w-5" />Sign In &amp; Generate</>}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            {/* Credit card */}
            <div className="bg-gradient-to-br from-gray-900 to-blue-950 rounded-2xl p-5 shadow-xl border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-yellow-400" />
                <h3 className="text-white font-black text-base">Credit Summary</h3>
              </div>
              {(user?.credits ?? 0) >= 1 ? (
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between text-gray-300">
                    <span>Episode cost</span>
                    <span className="font-bold text-white">1 credit</span>
                  </div>
                  <div className="flex justify-between text-gray-300">
                    <span>Your balance</span>
                    <span className="font-bold text-electric-green">{user?.credits ?? 0} credits</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between text-gray-300">
                    <span>After generation</span>
                    <span className="font-bold text-yellow-400">{Math.max(0, (user?.credits ?? 0) - 1)} credits</span>
                  </div>
                </div>
              ) : (
                <div className="text-sm mb-4">
                  <p className="text-gray-300 mb-2">
                    You have <span className="font-bold text-red-400">0 credits</span>. This episode will be a <span className="font-bold text-yellow-400">free 1-minute preview</span>.
                  </p>
                  <p className="text-gray-400 text-xs">Buy credits to unlock full episodes — including this preview once it's ready.</p>
                </div>
              )}
              <Link to="/#pricing" className="block w-full text-center bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black py-2.5 rounded-xl transition-colors text-sm">
                {(user?.credits ?? 0) >= 1 ? 'Buy More Credits' : 'Buy Credits'}
              </Link>
            </div>

            {/* Pro tips */}
            <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl p-5">
              <h3 className="font-black text-gray-200 mb-4 flex items-center gap-2 text-base"><Zap className="h-5 w-5 text-yellow-400" />Pro Tips</h3>
              <ul className="space-y-3">
                {[
                  { icon: '📄', text: 'Export a text-based PDF from GameChanger for best results.' },
                  { icon: '👤', text: 'Full player names on the roster give the best shoutouts.' },
                  { icon: '⭐', text: 'Use the highlights step to point out key moments.' },
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="shrink-0 w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center text-base">{tip.icon}</span>
                    <span>{tip.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Step checklist */}
            <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl p-5">
              <h3 className="font-black text-gray-200 mb-3 text-base flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-400" />Progress</h3>
              <ul className="space-y-2">
                {STEP_META.map(step => (
                  <li key={step.num} className={`flex items-center gap-3 text-sm font-medium transition-colors ${currentStep >= step.num ? 'text-gray-200' : 'text-gray-500'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${currentStep > step.num ? 'bg-electric-green' : currentStep === step.num ? 'bg-royal-blue ring-2 ring-royal-blue/30' : 'bg-white/15'}`}>
                      {currentStep > step.num
                        ? <CheckCircle className="w-3 h-3 text-white" />
                        : <span className="text-white text-xs font-black">{step.num}</span>
                      }
                    </span>
                    {step.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePage;
